import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Badge from '@/components/Typography/Badge';

/**
 * Subtle featured / new / beta labels for tool cards and list rows.
 *
 * `omit` lets a surface that already communicates a flag through layout
 * (e.g. a "Featured" section) suppress the redundant pill: `['featured']`,
 * `['new']`, `['beta']`.
 */
function ToolFlagBadges({ tool, omit = [] }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const flags = tool?.flags;
  if (!flags) return null;
  const f = {
    isFeatured: flags.isFeatured && !omit.includes('featured'),
    isNew: flags.isNew && !omit.includes('new'),
    isBeta: flags.isBeta && !omit.includes('beta'),
  };
  if (!f.isFeatured && !f.isNew && !f.isBeta) return null;

  const bg = colors.solid_color_tertiary;
  const basePill = {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 10,
    backgroundColor: bg,
    borderColor: colors.border_color_secondary,
  };

  return (
    <View style={styles.row}>
      {f.isFeatured && (
        <Badge
          text={t('explore.featured') || 'Featured'}
          isSmall
          textColor="secondary"
          backgroundColor={bg}
          style={basePill}
        />
      )}
      {f.isNew && (
        <Badge
          text={t('pages.tools.badgeNew') || 'New'}
          isSmall
          textColor="secondary"
          backgroundColor={bg}
          style={basePill}
        />
      )}
      {f.isBeta && (
        <Badge
          text={t('pages.tools.badgeBeta') || 'Beta'}
          isSmall
          textColor="tertiary"
          backgroundColor={bg}
          style={basePill}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 4,
  },
});

export default React.memo(ToolFlagBadges);
